import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  MapPin,
  DollarSign,
  Send,
  CheckCircle,
  Briefcase,
  Calendar,
  Globe,
  Mail,
  Phone,
  Linkedin,
  Github,
  ExternalLink,
  Loader2,
} from 'lucide-react'
import { talentService } from '../../services/talentService'
import { serviceRequestService } from '../../services/serviceRequestService'
import { seekerService } from '../../services/seekerService'
import { useAuthStore } from '../../stores/authStore'
import type { TalentProfileAnonymized, TalentProfileFull } from '../../types/talent'
import { ORGANIZATION_TYPE_OPTIONS, REQUEST_TYPE_OPTIONS } from '../../types/seeker'
import { AUDIENCE_OPTIONS } from '../../types/serviceRequest'
import toast from 'react-hot-toast'

export default function TalentProfilePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [talent, setTalent] = useState<TalentProfileAnonymized | TalentProfileFull | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isRequesting, setIsRequesting] = useState(false)
  const [requestSent, setRequestSent] = useState(false)
  const [message, setMessage] = useState('')
  const [selectedServiceType, setSelectedServiceType] = useState<string>('')

  // Enhanced form fields
  const [requesterFullName, setRequesterFullName] = useState('')
  const [requesterEmail, setRequesterEmail] = useState('')
  const [requesterOrgType, setRequesterOrgType] = useState('')
  const [requestType, setRequestType] = useState('')
  const [topic, setTopic] = useState('')
  const [intendedAudience, setIntendedAudience] = useState<string[]>([])

  useEffect(() => {
    if (id) {
      loadTalent()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Pre-populate form fields from seeker profile
  useEffect(() => {
    if (user?.role !== 'seeker') return
    seekerService.getMyProfile().then((profile) => {
      const fullName = [profile.user_first_name, profile.user_last_name].filter(Boolean).join(' ')
      if (fullName) setRequesterFullName(fullName)
      if (profile.user_email) setRequesterEmail(profile.user_email)
      if (profile.organization_type) setRequesterOrgType(profile.organization_type)
    }).catch(() => {
      // Seeker profile may not exist yet, that's fine
    })
  }, [user?.role])

  const loadTalent = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await talentService.getTalent(id!)
      setTalent(data)
    } catch (err: unknown) {
      const error = err as { response?: { status?: number } }
      if (error.response?.status === 404) {
        setError('Expert not found')
      } else {
        setError('Failed to load expert profile')
      }
      console.error('Error loading talent:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAudienceToggle = (value: string) => {
    setIntendedAudience((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }

  const handleSendRequest = async () => {
    if (!id || !message.trim()) {
      toast.error('Please describe what you need help with')
      return
    }

    try {
      setIsRequesting(true)
      await serviceRequestService.createRequest({
        talent_id: id,
        service_type_id: selectedServiceType ? parseInt(selectedServiceType) : null,
        message: message.trim(),
        requester_full_name: requesterFullName.trim() || undefined,
        requester_email: requesterEmail.trim() || undefined,
        requester_organization_type: requesterOrgType || undefined,
        request_type: requestType || undefined,
        topic: topic.trim() || undefined,
        intended_audience: intendedAudience.length > 0 ? intendedAudience : undefined,
      })
      setRequestSent(true)
      toast.success('Service request sent! The expert will respond with a quote.')
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string; detail?: string } } }
      if (error.response?.data?.error) {
        toast.error(error.response.data.error)
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail)
      } else {
        toast.error('Failed to send request')
      }
    } finally {
      setIsRequesting(false)
    }
  }

  // Get initials for avatar
  const getInitials = () => {
    if (!talent) return 'T'
    // Use first and last name if available
    if (talent.first_name && talent.last_name) {
      return `${talent.first_name[0]}${talent.last_name[0]}`.toUpperCase()
    }
    if (talent.first_name) {
      return talent.first_name.slice(0, 2).toUpperCase()
    }
    // Fallback to display_id
    const parts = talent.display_id.split('-')
    return parts[1]?.slice(0, 2).toUpperCase() || 'T'
  }

  // Get display name (full name or fallback to display_id)
  const getDisplayName = () => {
    if (!talent) return ''
    if (talent.full_name) return talent.full_name
    if (talent.first_name && talent.last_name) return `${talent.first_name} ${talent.last_name}`
    if (talent.first_name) return talent.first_name
    return talent.display_id
  }

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="animate-pulse">
          <div className="h-6 w-32 bg-secondary-200 rounded mb-8" />
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="card bg-primary-900 p-8">
                <div className="flex items-center gap-6">
                  <div className="h-20 w-20 rounded-full bg-primary-700" />
                  <div className="flex-1">
                    <div className="h-8 w-48 bg-primary-700 rounded mb-3" />
                    <div className="h-5 w-64 bg-primary-800 rounded" />
                  </div>
                </div>
              </div>
              <div className="card p-8">
                <div className="h-6 w-24 bg-secondary-200 rounded mb-4" />
                <div className="h-20 w-full bg-secondary-100 rounded" />
              </div>
            </div>
            <div className="space-y-6">
              <div className="card p-6">
                <div className="h-32 bg-secondary-100 rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !talent) {
    return (
      <div className="max-w-5xl mx-auto">
        <Link to="/talents" className="inline-flex items-center text-secondary-600 hover:text-secondary-900 mb-8 font-medium">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Search
        </Link>
        <div className="card p-12 text-center">
          <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="h-8 w-8 text-secondary-400" />
          </div>
          <h2 className="text-xl font-semibold text-secondary-900 mb-2">{error || 'Expert not found'}</h2>
          <p className="text-secondary-500 mb-6">The profile you're looking for doesn't exist or has been removed.</p>
          <button onClick={() => navigate('/talents')} className="btn btn-primary">
            Browse Experts
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto">
      <Link to="/talents" className="inline-flex items-center text-secondary-600 hover:text-primary-600 mb-8 font-medium transition-colors">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Search
      </Link>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Dark Header Card */}
          <div className="card bg-primary-900 text-white overflow-hidden">
            <div className="p-8">
              <div className="flex flex-col sm:flex-row items-start gap-6">
                {/* Avatar */}
                <div className="h-20 w-20 rounded-full bg-primary-700 flex items-center justify-center ring-2 ring-primary-600 overflow-hidden">
                  {talent.profile_picture_url ? (
                    <img src={talent.profile_picture_url} alt={getDisplayName()} className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-white text-2xl font-bold">
                      {getInitials()}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  {/* Main field badges */}
                  {talent.main_fields && talent.main_fields.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {talent.main_fields.map((field) => (
                        <span key={field.id} className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-accent-500 text-white">
                          {field.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Name */}
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h1 className="text-2xl font-bold text-white">{getDisplayName()}</h1>
                  </div>
                  <p className="text-primary-200 mt-1">{talent.headline}</p>
                  {/* Meta info row */}
                  <div className="flex flex-wrap gap-4 mt-4 text-sm text-primary-300">
                    {talent.country && (
                      <span className="flex items-center gap-1.5">
                        <MapPin className="h-4 w-4" />
                        {talent.country}
                      </span>
                    )}
                    {talent.timezone && (
                      <span className="flex items-center gap-1.5">
                        <Globe className="h-4 w-4" />
                        {talent.timezone}
                      </span>
                    )}
                    {talent.hourly_rate_display && (
                      <span className="flex items-center gap-1.5 text-accent-400 font-medium">
                        <DollarSign className="h-4 w-4" />
                        {talent.hourly_rate_display}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* About */}
          {talent.bio && (
            <div className="card">
              <div className="card-header">
                <h2 className="font-semibold text-lg">About</h2>
              </div>
              <div className="card-body">
                <p className="text-secondary-600 whitespace-pre-line">{talent.bio}</p>
              </div>
            </div>
          )}

          {/* Expertise Offerings */}
          {talent.expertise_offerings && talent.expertise_offerings.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h2 className="font-semibold text-lg">Expertise Offerings</h2>
              </div>
              <div className="card-body">
                <div className="flex flex-wrap gap-2">
                  {talent.expertise_offerings.map((offering) => (
                    <span
                      key={offering.id}
                      className="px-4 py-2 bg-accent-50 text-accent-700 rounded-lg text-sm font-medium border border-accent-100"
                    >
                      {offering.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Skills */}
          {talent.skills && talent.skills.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h2 className="font-semibold text-lg">Skills</h2>
              </div>
              <div className="card-body">
                <div className="flex flex-wrap gap-2">
                  {talent.skills.map((skill) => (
                    <span
                      key={typeof skill === 'string' ? skill : skill.id}
                      className="px-4 py-2 bg-primary-50 text-primary-700 rounded-lg text-sm font-medium border border-primary-100"
                    >
                      {typeof skill === 'string' ? skill : skill.skill?.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Languages */}
          {talent.languages && talent.languages.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h2 className="font-semibold text-lg">Languages</h2>
              </div>
              <div className="card-body">
                <div className="flex flex-wrap gap-2">
                  {talent.languages.map((lang) => (
                    <span
                      key={lang.id}
                      className="px-4 py-2 bg-secondary-100 text-secondary-700 rounded-lg text-sm font-medium"
                    >
                      {lang.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Portfolio */}
          {talent.portfolio_items && talent.portfolio_items.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h2 className="font-semibold text-lg">Portfolio</h2>
              </div>
              <div className="card-body">
                <div className="grid gap-4 sm:grid-cols-2">
                  {talent.portfolio_items.map((item) => (
                    <div key={item.id} className="border border-secondary-200 rounded-lg p-4">
                      <h3 className="font-semibold text-secondary-900">{item.title}</h3>
                      {item.description && (
                        <p className="text-secondary-500 text-sm mt-1 line-clamp-2">{item.description}</p>
                      )}
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm mt-2"
                        >
                          View Project <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Experience */}
          {talent.experiences && talent.experiences.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h2 className="font-semibold text-lg">Experience</h2>
              </div>
              <div className="card-body space-y-6">
                {talent.experiences.map((exp) => (
                  <div key={exp.id} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="h-6 w-6 text-secondary-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap justify-between items-start gap-2">
                        <div>
                          <h3 className="font-semibold text-secondary-900">{exp.title}</h3>
                          <p className="text-secondary-600">{exp.company_display}</p>
                        </div>
                        <span className="text-sm text-secondary-500 flex items-center gap-1.5">
                          <Calendar className="h-4 w-4" />
                          {exp.start_date} - {exp.is_current ? 'Present' : exp.end_date}
                        </span>
                      </div>
                      {exp.description && (
                        <p className="text-secondary-500 text-sm mt-2">{exp.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Contact Information (public now) */}
          {'contact_email' in talent && (talent as TalentProfileFull).contact_email && (
            <div className="card">
              <div className="card-header">
                <h3 className="font-semibold">Contact Information</h3>
              </div>
              <div className="card-body space-y-3 text-sm">
                {(talent as TalentProfileFull).contact_email && (
                  <a
                    href={`mailto:${(talent as TalentProfileFull).contact_email}`}
                    className="flex items-center gap-3 text-secondary-600 hover:text-primary-600 transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                    {(talent as TalentProfileFull).contact_email}
                  </a>
                )}
                {(talent as TalentProfileFull).phone && (
                  <a
                    href={`tel:${(talent as TalentProfileFull).phone}`}
                    className="flex items-center gap-3 text-secondary-600 hover:text-primary-600 transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    {(talent as TalentProfileFull).phone}
                  </a>
                )}
                {(talent as TalentProfileFull).linkedin_url && (
                  <a
                    href={(talent as TalentProfileFull).linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-secondary-600 hover:text-primary-600 transition-colors"
                  >
                    <Linkedin className="h-4 w-4" />
                    LinkedIn Profile
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {(talent as TalentProfileFull).github_url && (
                  <a
                    href={(talent as TalentProfileFull).github_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-secondary-600 hover:text-primary-600 transition-colors"
                  >
                    <Github className="h-4 w-4" />
                    GitHub Profile
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Engagement Preferences */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold">Engagement Preferences</h3>
            </div>
            <div className="card-body space-y-3 text-sm">
              {talent.experience_level && (
                <div className="flex justify-between">
                  <span className="text-secondary-500">Experience Level</span>
                  <span className="font-medium text-secondary-900">
                    {talent.experience_level.name}
                  </span>
                </div>
              )}
              {talent.preferred_work_types && talent.preferred_work_types.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-secondary-500">Work Types</span>
                  <span className="font-medium text-secondary-900">
                    {talent.preferred_work_types.join(', ')}
                  </span>
                </div>
              )}
              {talent.work_experience_types && talent.work_experience_types.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-secondary-500">Experience In</span>
                  <span className="font-medium text-secondary-900 text-right">
                    {talent.work_experience_types.map(w => w.name).join(', ')}
                  </span>
                </div>
              )}
              {talent.preferred_formats && talent.preferred_formats.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-secondary-500">Formats</span>
                  <span className="font-medium text-secondary-900 text-right">
                    {talent.preferred_formats.map(f => f.name).join(', ')}
                  </span>
                </div>
              )}
              {talent.preferred_times && talent.preferred_times.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-secondary-500">Available Times</span>
                  <span className="font-medium text-secondary-900 text-right">
                    {talent.preferred_times.map(t => t.name).join(', ')}
                  </span>
                </div>
              )}
              {talent.hourly_rate_display && (
                <div className="flex justify-between">
                  <span className="text-secondary-500">Rate</span>
                  <span className="font-medium text-accent-600">{talent.hourly_rate_display}</span>
                </div>
              )}
            </div>
          </div>

          {/* Background */}
          {((talent.countries_professional && talent.countries_professional.length > 0) ||
            (talent.countries_education && talent.countries_education.length > 0)) && (
            <div className="card">
              <div className="card-header">
                <h3 className="font-semibold">Background</h3>
              </div>
              <div className="card-body space-y-3 text-sm">
                {talent.countries_professional && talent.countries_professional.length > 0 && (
                  <div>
                    <span className="text-secondary-500 block mb-1">Professional Experience</span>
                    <span className="font-medium text-secondary-900">
                      {talent.countries_professional.map(c => c.name).join(', ')}
                    </span>
                  </div>
                )}
                {talent.countries_education && talent.countries_education.length > 0 && (
                  <div>
                    <span className="text-secondary-500 block mb-1">Education</span>
                    <span className="font-medium text-secondary-900">
                      {talent.countries_education.map(c => c.name).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - Request Service Only */}
        <div className="space-y-6">
          <div className="card sticky top-6">
            <div className="card-header">
              <h3 className="font-semibold">Request Service</h3>
            </div>
            <div className="card-body">
              {requestSent ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-secondary-900 mb-2">Request Sent!</h3>
                  <p className="text-secondary-500 text-sm">
                    The expert will review your request and respond with a quote.
                  </p>
                  <Link to="/connections" className="btn btn-secondary w-full mt-4">
                    View My Requests
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Full Name */}
                  <div>
                    <label className="label">Full Name</label>
                    <input
                      type="text"
                      value={requesterFullName}
                      onChange={(e) => setRequesterFullName(e.target.value)}
                      placeholder="Your full name"
                      className="input"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="label">Email</label>
                    <input
                      type="email"
                      value={requesterEmail}
                      onChange={(e) => setRequesterEmail(e.target.value)}
                      placeholder="Your email address"
                      className="input"
                    />
                  </div>

                  {/* Who is requesting */}
                  <div>
                    <label className="label">Who is requesting</label>
                    <select
                      value={requesterOrgType}
                      onChange={(e) => setRequesterOrgType(e.target.value)}
                      className="input"
                    >
                      <option value="">Select (optional)</option>
                      {ORGANIZATION_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Type of request */}
                  <div>
                    <label className="label">Type of request</label>
                    <select
                      value={requestType}
                      onChange={(e) => setRequestType(e.target.value)}
                      className="input"
                    >
                      <option value="">Select (optional)</option>
                      {REQUEST_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Service type (from expert's offerings) */}
                  {talent.expertise_offerings && talent.expertise_offerings.length > 0 && (
                    <div>
                      <label className="label">Type of request</label>
                      <select
                        value={selectedServiceType}
                        onChange={(e) => setSelectedServiceType(e.target.value)}
                        className="input"
                      >
                        <option value="">Select (optional)</option>
                        {talent.expertise_offerings.map((offering) => (
                          <option key={offering.id} value={offering.id}>
                            {offering.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Topic */}
                  <div>
                    <label className="label">Topic or focus area</label>
                    <input
                      type="text"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      placeholder="e.g. Machine Learning, EU Policy..."
                      className="input"
                    />
                  </div>

                  {/* Intended Audience */}
                  <div>
                    <label className="label">Who is this for?</label>
                    <div className="space-y-2 mt-1">
                      {AUDIENCE_OPTIONS.map((opt) => (
                        <label key={opt.value} className="flex items-center gap-2 text-sm text-secondary-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={intendedAudience.includes(opt.value)}
                            onChange={() => handleAudienceToggle(opt.value)}
                            className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="label">What do you need help with? *</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Describe your project, questions, or what kind of consultation you're looking for..."
                      className="input min-h-[120px] resize-none"
                      rows={5}
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    onClick={handleSendRequest}
                    disabled={isRequesting || !message.trim()}
                    className="btn btn-accent w-full py-3"
                  >
                    {isRequesting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Request
                      </>
                    )}
                  </button>

                  {/* Helper text */}
                  <p className="text-xs text-secondary-400 text-center">
                    The expert will respond with a quote. You only pay if you accept the price.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
